import { useQuery } from '@apollo/client/react';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ClassListFiltersDocument } from '../../../graphql/types';
import { useClassesQueryVariables } from '../../../hooks/useQueryParams';
import useTracking from '../../../hooks/useTracking';
import { BORDER_GRAY, WHITE } from '../../../theme';
import {
  EventCategories,
  EventNames,
  EventSectionNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import { CheckboxGroup, CheckboxOptionType } from '../../reusable/Checkbox';

const StyledSidebar = styled.aside`
  width: 295px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: sticky;
  top: 104px;
  max-height: 60vh;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 16px;
  background-color: ${WHITE};
  border-radius: 8px;
  border: 1px solid ${BORDER_GRAY};
`;

interface FiltersSidebarProps {}

export const FiltersSidebar: React.FC<FiltersSidebarProps> = () => {
  const [queryParams, setQueryParams] = useClassesQueryVariables();
  const { data } = useQuery(ClassListFiltersDocument);
  const { track } = useTracking();

  const handleStudioOptionClick = (checkboxValue: CheckboxOptionType) => {
    track(EventNames.FilterClick, {
      event_category: EventCategories.Classes,
      event_label: checkboxValue.label,
      event_subcategory: queryParams.studioIds?.includes(checkboxValue.value)
        ? EventSubCategories.Off
        : EventSubCategories.On,
      section_name: EventSectionNames.Studios,
    });

    const newQueryParams = {
      ...queryParams,
    };

    if (newQueryParams.studioIds?.includes(checkboxValue.value)) {
      // Remove studio and all its locations
      newQueryParams.studioIds = newQueryParams.studioIds.filter(
        (id) => id !== checkboxValue.value,
      );

      // Find and remove all locations for this studio
      const studio = studios.find((s) => s?.id === checkboxValue.value);
      const studioLocationIds =
        studio?.locations?.edges?.map(({ node }) => node?.id) || [];
      newQueryParams.locationIds = (newQueryParams.locationIds || []).filter(
        (id) => !studioLocationIds.includes(id),
      );
    } else {
      // Add studio and all its locations
      newQueryParams.studioIds = [
        ...(newQueryParams.studioIds || []),
        checkboxValue.value,
      ];

      // Find and add all locations for this studio
      const studio = studios.find((s) => s?.id === checkboxValue.value);
      const studioLocationIds =
        studio?.locations?.edges?.map(({ node }) => node?.id) || [];
      newQueryParams.locationIds = [
        ...new Set([
          ...(newQueryParams.locationIds || []),
          ...studioLocationIds,
        ]),
      ];
    }

    setQueryParams(newQueryParams);
  };

  const handleLocationOptionClick = (checkboxValue: CheckboxOptionType) => {
    track(EventNames.FilterClick, {
      event_category: EventCategories.Classes,
      event_label: checkboxValue.label,
      event_subcategory: queryParams.locationIds?.includes(checkboxValue.value)
        ? EventSubCategories.Off
        : EventSubCategories.On,
      section_name: EventSectionNames.Locations,
    });

    const newQueryParams = {
      ...queryParams,
    };

    // Find the parent studio for this location
    const locationOption = hierarchicalStudioLocationOptions.find(
      (opt) => opt.value === checkboxValue.value && opt.parentValue,
    );
    const parentStudioId = locationOption?.parentValue;

    if (newQueryParams.locationIds?.includes(checkboxValue.value)) {
      // Remove location
      newQueryParams.locationIds = newQueryParams.locationIds.filter(
        (id) => id !== checkboxValue.value,
      );

      // Remove parent studio if it was selected
      if (
        parentStudioId &&
        newQueryParams.studioIds?.includes(parentStudioId)
      ) {
        newQueryParams.studioIds = newQueryParams.studioIds.filter(
          (id) => id !== parentStudioId,
        );
      }
    } else {
      // Add location
      newQueryParams.locationIds = [
        ...(newQueryParams.locationIds || []),
        checkboxValue.value,
      ];

      // Check if all locations of the parent studio are now selected
      if (parentStudioId) {
        const studio = studios.find((s) => s?.id === parentStudioId);
        const allStudioLocationIds =
          studio?.locations?.edges?.map(({ node }) => node?.id) || [];
        const selectedLocationIds = [
          ...(newQueryParams.locationIds || []),
          checkboxValue.value,
        ];

        const allLocationsSelected = allStudioLocationIds.every((locId) =>
          selectedLocationIds.includes(locId),
        );

        // If all locations are selected, also select the parent studio
        if (
          allLocationsSelected &&
          !newQueryParams.studioIds?.includes(parentStudioId)
        ) {
          newQueryParams.studioIds = [
            ...(newQueryParams.studioIds || []),
            parentStudioId,
          ];
        }
      }
    }

    setQueryParams(newQueryParams);
  };

  const clients =
    data?.self?.coachData?.clients.edges.map((edge) => edge.node) || [];

  // de-duplicate studios to make sure only one central studio is shown
  const studios = uniqBy(
    clients.flatMap((client) => client?.studios || []),
    'id',
  );

  const hierarchicalStudioLocationOptions = useMemo(() => {
    const options: Array<{
      label: string;
      value: string;
      parentValue?: string;
      children?: string[];
    }> = [];

    studios.forEach((studio) => {
      // Add the studio as a parent option
      options.push({
        label: studio?.name || '',
        value: studio?.id || '',
        children: studio?.locations?.edges?.map(({ node }) => node?.id) || [],
      });

      // Add each location as a child option immediately after its parent studio
      studio?.locations?.edges?.forEach(({ node }) => {
        if (node) {
          options.push({
            label: node.name || '',
            value: node.id || '',
            parentValue: studio?.id || '',
          });
        }
      });
    });

    return options;
  }, [studios]);

  return (
    <div data-testid="classes-filters-sidebar">
      <StyledSidebar>
        <CheckboxGroup
          label="Studios & Locations"
          name="studio-location"
          checkedOptions={[
            ...(queryParams.studioIds ?? []),
            ...(queryParams.locationIds ?? []),
          ]}
          options={hierarchicalStudioLocationOptions}
          onChange={(checkboxValue) => {
            // Check if it's a studio or location by checking if it has a parentValue
            const hasParent = hierarchicalStudioLocationOptions.find(
              (opt) => opt.value === checkboxValue.value && opt.parentValue,
            );

            if (hasParent) {
              handleLocationOptionClick(checkboxValue);
            } else {
              handleStudioOptionClick(checkboxValue);
            }
          }}
        />
      </StyledSidebar>
    </div>
  );
};
